import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { AppRouter } from './app.router';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app.component';
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { RequestLoaderInterceptor } from './core/interceptors/request-loader.interceptor';
import { environment } from '../environments/environment';
import { NgxsModule } from '@ngxs/store';
import { NgxsResetPluginModule } from 'ngxs-reset-plugin';
import { AppState } from './store/state';
import { AuthorizationState } from './modules/authorization/store/state';
import { DashboardState } from 'src/app/modules/logged/dashboard/store/state';
import { WorklocationState } from './modules/logged/work-locations/store/state';
import { SubcontractorState } from './modules/logged/subcontractors/store/state';
import { PayrollState } from './modules/logged/payrolls/store/state';
import { SettingsState } from './modules/logged/settings/store/state';
import { CompanyState } from './modules/logged/companies/store/state';
import { InvoiceState } from './modules/logged/invoices/store/state';
import { LogState } from './modules/logged/logs/store/state';
import { MessageState } from './modules/logged/messages/store/state';
import { TimeRecordsState } from './modules/logged/time-records/store/state';
import { InvitationState } from './modules/logged/invitations/store/state';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AgreementState } from 'src/app/modules/logged/agreements/store/state';
import { MainInterceptor } from 'src/app/core/interceptors/main.interceptor';
import { SovereignDetailsState } from './modules/logged/sovereign-details/store/state';

import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        AppRouter,
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        MatSnackBarModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        NgxsModule.forRoot(
            [
                AppState,
                AuthorizationState,
                InvitationState,
                DashboardState,
                WorklocationState,
                SubcontractorState,
                PayrollState,
                SettingsState,
                CompanyState,
                InvoiceState,
                LogState,
                MessageState,
                TimeRecordsState,
                AgreementState,
                SovereignDetailsState,
            ],
            { developmentMode: !environment.production },
        ),
        NgxsResetPluginModule.forRoot(),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            // Register the ServiceWorker as soon as the app is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000',
        }),
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MainInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RequestLoaderInterceptor,
            multi: true,
        },
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: { duration: 2500 },
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: true,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
